import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import TableFooter from '@/components/TableFooter/TableFooter'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { AuditLogDetailPanel } from '@/features/AuditLogs/components/AuditLogDetailPanel/AuditLogDetailPanel'
import { AuditLogsFilters } from '@/features/AuditLogs/components/AuditLogsFilters/AuditLogsFilters'
import { AuditLogsTable } from '@/features/AuditLogs/components/AuditLogsTable/AuditLogsTable'
import {
  type AuditLogStatus,
  type DateRangePreset,
  useAuditLogFilters,
} from '@/features/AuditLogs/hooks/useAuditLogFilters'
import { useGetAuditLogs } from '@/generated/http-clients/v3ApiComponents'
import type { AuditLogs } from '@/generated/http-clients/v3ApiSchemas'
import { FIRST_PAGE } from '@/helpers/constants'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  toaster,
} from '@/hooks/useToast/toastUtils'
import { auditLogsRootRoute } from '@/router/rootRoutes'
import {
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Icon,
  Separator,
  Splitter,
  Switch,
  Text,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { createRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { Link2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const auditLogsSearchParams = z.object({
  page: z.number().optional().default(1),
  dateRange: z
    .enum(['lastHour', 'last24Hours', 'last7Days', 'last30Days'])
    .optional()
    .default('last24Hours'),
  hiveId: z.string().optional(),
  orgId: z.string().optional(),
  user: z.string().optional(),
  status: z.enum(['success', 'error']).optional(),
})

export type AuditLogsSearchParams = z.infer<typeof auditLogsSearchParams>

export const auditLogsRoute = createRoute({
  getParentRoute: () => auditLogsRootRoute,
  path: '/',
  component: AuditLogsPage,
  validateSearch: auditLogsSearchParams,
})

const AUTO_REFRESH_INTERVAL = 30000 // 30 seconds

// Splitter layout constants
const SPLITTER_COLLAPSED = [100, 0] as const
const SPLITTER_EXPANDED = [65, 35] as const

function AuditLogsPage() {
  const { t } = useTranslation('features', { useSuspense: false })
  const navigate = useNavigate()
  const search = useSearch({ from: auditLogsRoute.id })

  // Local state
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [selectedLog, setSelectedLog] = useState<AuditLogs | null>(null)

  // Extract search params with defaults
  const page = search.page ?? 1
  const dateRangePreset = (search.dateRange ?? 'last24Hours') as DateRangePreset
  const hiveId = search.hiveId ?? null
  const orgId = search.orgId ?? null
  const user = search.user ?? null
  const statusFilter = (search.status ?? null) as AuditLogStatus | null

  // Get enhanced query params from the filter hook
  const { enhancedQueryParams, hasActiveFilters } = useAuditLogFilters({
    page,
    dateRangePreset,
    hiveId,
    orgId,
    user,
    status: statusFilter,
  })

  // Fetch audit logs
  const auditLogs = useGetAuditLogs(
    { queryParams: enhancedQueryParams },
    {
      refetchInterval: autoRefresh ? AUTO_REFRESH_INTERVAL : false,
      refetchOnWindowFocus: false,
    },
  )

  // Navigation helpers
  const updateSearchParams = (updates: Partial<AuditLogsSearchParams>) => {
    navigate({
      to: auditLogsRoute.to,
      search: (prev) => ({ ...prev, ...updates, page: updates.page ?? 1 }),
      replace: true,
    })
  }

  const handleNextPage = () => {
    if (auditLogs.data?.has_next_page) {
      updateSearchParams({ page: page + 1 })
    }
  }

  const handlePreviousPage = () => {
    if (page > 1) {
      updateSearchParams({ page: page - 1 })
    }
  }

  const goToFirstPage = () => {
    updateSearchParams({ page: FIRST_PAGE })
  }

  const goToLastPage = () => {
    const totalPages = auditLogs.data?.total_pages ?? page
    updateSearchParams({ page: totalPages })
  }

  // Filter handlers
  const handleDateRangeChange = (preset: DateRangePreset) => {
    updateSearchParams({ dateRange: preset })
  }

  const handleHiveIdChange = (value: string | null) => {
    updateSearchParams({ hiveId: value ?? undefined })
  }

  const handleOrgIdChange = (value: string | null) => {
    updateSearchParams({ orgId: value ?? undefined })
  }

  const handleUserChange = (value: string | null) => {
    updateSearchParams({ user: value ?? undefined })
  }

  const handleStatusChange = (value: AuditLogStatus | null) => {
    updateSearchParams({ status: value ?? undefined })
  }

  const handleClearFilters = () => {
    navigate({
      to: auditLogsRoute.to,
      search: { page: 1, dateRange: 'last24Hours' },
      replace: true,
    })
  }

  const handleCopyUrl = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        toaster.create({
          ...SUCCESS_TOAST_OPTION,
          title: String(t('auditLogs.copyUrl.success')),
        })
      })
      .catch(() => {
        toaster.create({
          ...ERROR_TOAST_OPTION,
          title: String(t('auditLogs.copyUrl.error')),
        })
      })
  }

  const skeletonWidths = [150, 200, 300, 100]

  // Controlled splitter sizes for programmatic collapse
  // Start collapsed (100% table) - opens when a row is clicked
  const [splitterSizes, setSplitterSizes] = useState<number[]>([
    ...SPLITTER_COLLAPSED,
  ])
  const isDetailsPanelOpen = splitterSizes[1] > 0

  // Row click handler - select log and open panel if collapsed
  const handleRowClick = useCallback((log: AuditLogs) => {
    setSelectedLog(log)
    // Open details panel if collapsed
    setSplitterSizes((prev) => (prev[1] === 0 ? [...SPLITTER_EXPANDED] : prev))
  }, [])

  const handleCollapseDetails = useCallback(() => {
    setSplitterSizes([...SPLITTER_COLLAPSED])
  }, [])

  return (
    <PageLayout
      title={t('auditLogs.title')}
      action={
        <HStack gap={4}>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyUrl}
            colorPalette="gray"
            aria-label={String(t('auditLogs.copyUrl.button'))}
          >
            <Icon as={Link2} />
            {t('auditLogs.copyUrl.button')}
          </Button>
          <HStack gap={2}>
            <Text fontSize="sm" color="gray.600">
              {t('auditLogs.autoRefresh.label')}
            </Text>
            <Switch.Root
              checked={autoRefresh}
              onCheckedChange={(e) => setAutoRefresh(e.checked)}
              colorPalette="blue"
              size="md"
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Root>
          </HStack>
        </HStack>
      }
    >
      <Flex direction="column" h="100%" w="100%">
        <Box mb={4}>
          <AuditLogsFilters
            dateRangePreset={dateRangePreset}
            hiveId={hiveId}
            orgId={orgId}
            user={user}
            status={statusFilter}
            hasActiveFilters={hasActiveFilters}
            onDateRangeChange={handleDateRangeChange}
            onHiveIdChange={handleHiveIdChange}
            onOrgIdChange={handleOrgIdChange}
            onUserChange={handleUserChange}
            onStatusChange={handleStatusChange}
            onClearFilters={handleClearFilters}
          />
        </Box>

        {/* Divider between filters and content */}
        <Separator />

        <Splitter.Root
          size={splitterSizes}
          onResize={(details) => setSplitterSizes(details.size)}
          panels={[
            { id: 'table', minSize: 40 },
            { id: 'details', collapsible: true, collapsedSize: 0, minSize: 20 },
          ]}
          flex="1"
          minH="500px"
          borderTopWidth="0"
        >
          {/* Table Panel */}
          <Splitter.Panel id="table">
            <Box
              h="100%"
              display="flex"
              flexDirection="column"
              pt={4}
              pr={isDetailsPanelOpen ? 4 : 0}
            >
              <Card.Root flex="1" overflow="auto">
                <AuditLogsTable
                  logs={auditLogs.data?.audit_logs ?? []}
                  isLoading={auditLogs.isLoading}
                  isError={auditLogs.isError || !auditLogs.data}
                  isEmpty={
                    auditLogs.isSuccess &&
                    (auditLogs.data?.audit_logs?.length ?? 0) === 0
                  }
                  skeletonWidths={skeletonWidths}
                  selectedLogId={selectedLog?.id}
                  onRowClick={handleRowClick}
                  errorComponent={
                    <ApiErrorState
                      apiResource={auditLogs as unknown as UseQueryResult}
                      errorTitle={t('auditLogs.errors.title')}
                      errorDescription={t('auditLogs.errors.description')}
                    />
                  }
                  emptyMessage={t('auditLogs.table.noResults')}
                />
              </Card.Root>

              {auditLogs.isSuccess &&
                (auditLogs.data?.audit_logs?.length ?? 0) > 0 && (
                  <Box pt={4}>
                    <TableFooter
                      page={page}
                      totalPages={auditLogs.data?.total_pages ?? 1}
                      hasNextPage={auditLogs.data?.has_next_page ?? false}
                      onFirstPage={goToFirstPage}
                      onPreviousPage={handlePreviousPage}
                      onNextPage={handleNextPage}
                      onLastPage={goToLastPage}
                    />
                  </Box>
                )}
            </Box>
          </Splitter.Panel>

          {/* Only show resize trigger when panel is open */}
          {isDetailsPanelOpen && <Splitter.ResizeTrigger id="table:details" />}

          {/* Detail Panel */}
          <Splitter.Panel id="details">
            <Box
              h="100%"
              overflow="hidden"
              bg="white"
              borderLeftWidth={isDetailsPanelOpen ? '1px' : '0'}
            >
              <AuditLogDetailPanel
                log={selectedLog}
                onCollapse={handleCollapseDetails}
              />
            </Box>
          </Splitter.Panel>
        </Splitter.Root>
      </Flex>
    </PageLayout>
  )
}
